// Preventive-maintenance catalog — single source of truth, shared by the
// landing Plans section and the plan quiz. Mirrors the printed catalog
// (~/Desktop/TRC, "The full catalog, plan by plan"). `from` is the lowest
// tier that includes the task; higher tiers inherit it. 'quote' = à la carte,
// quotable at any tier (not part of a plan's included tasks).

export type SystemKey = 'water' | 'electrical' | 'climate' | 'structure' | 'garden' | 'tech' | 'alacarte'
export type CatalogTier = 'essential' | 'premium' | 'elite'
export type InclusionKey = CatalogTier | 'quote'

export interface Service {
  name:   string
  system: SystemKey
  from:   InclusionKey
}

export const SYSTEM_ORDER: SystemKey[] = ['water', 'electrical', 'climate', 'structure', 'garden', 'tech', 'alacarte']

export const SYSTEM_LABELS: Record<'en' | 'es', Record<SystemKey, string>> = {
  en: {
    water: 'Plumbing & water', electrical: 'Electrical', climate: 'A/C & climate',
    structure: 'Structure & exterior', garden: 'Pool & garden', tech: 'Security & wastewater',
    alacarte: 'On request',
  },
  es: {
    water: 'Plomería y agua', electrical: 'Eléctrico', climate: 'A/C y clima',
    structure: 'Estructura y exterior', garden: 'Piscina y jardín', tech: 'Seguridad y aguas',
    alacarte: 'A pedido',
  },
}

const TIER_RANK: Record<CatalogTier, number> = { essential: 0, premium: 1, elite: 2 }

// True if a plan tier includes a task (cumulative). À la carte ('quote') is
// never "included" — it's quoted separately.
export function planIncludes(serviceFrom: InclusionKey, tier: CatalogTier): boolean {
  if (serviceFrom === 'quote') return false
  return TIER_RANK[tier] >= TIER_RANK[serviceFrom]
}

export const CATALOG: Service[] = [
  // Plumbing & water
  { name: 'Cistern capacity check (pre-dry season)',        system: 'water', from: 'elite'     },
  { name: 'Cistern sediment cleaning',                      system: 'water', from: 'premium'   },
  { name: 'Pump service',                                   system: 'water', from: 'premium'   },
  { name: 'Washing machine hose cleaning',                  system: 'water', from: 'premium'   },
  { name: 'Dryer duct cleaning',                            system: 'water', from: 'premium'   },
  { name: 'Shutoff valve exercise',                         system: 'water', from: 'premium'   },
  { name: 'Water heater cleaning and testing',             system: 'water', from: 'premium'   },
  { name: 'Leak inspection (sinks, toilets, connections)',  system: 'water', from: 'essential' },
  { name: 'Water pump listening check',                     system: 'water', from: 'essential' },
  { name: 'Water quality/TDS test',                         system: 'water', from: 'essential' },
  { name: 'Water filter cartridge change',                  system: 'water', from: 'essential' },
  { name: 'Water heater check',                             system: 'water', from: 'essential' },
  { name: 'Shower drain cleaning',                          system: 'water', from: 'essential' },
  { name: 'Shower head cleaning',                           system: 'water', from: 'essential' },
  { name: 'Cistern check valve inspection',                 system: 'water', from: 'essential' },
  { name: 'Toilet check',                                   system: 'water', from: 'essential' },
  { name: 'Faucet check',                                   system: 'water', from: 'essential' },
  { name: 'Cistern check',                                  system: 'water', from: 'essential' },
  { name: 'Pump service check',                             system: 'water', from: 'essential' },

  // Electrical
  { name: 'Insulation resistance test (megger)',            system: 'electrical', from: 'elite'     },
  { name: 'Sensor & timer light test',                      system: 'electrical', from: 'elite'     },
  { name: 'Bulb replacement',                               system: 'electrical', from: 'premium'   },
  { name: 'Electrical panel terminal tightening',           system: 'electrical', from: 'premium'   },
  { name: 'Breaker exercise',                               system: 'electrical', from: 'premium'   },
  { name: 'Inverter / UPS & battery check',                 system: 'electrical', from: 'premium'   },
  { name: 'Exterior lighting check (pre-dry season)',       system: 'electrical', from: 'premium'   },
  { name: 'Outlet voltage test',                            system: 'electrical', from: 'essential' },
  { name: 'GFCI test in baths & kitchen',                   system: 'electrical', from: 'essential' },
  { name: 'Electrical panel terminal check',               system: 'electrical', from: 'essential' },
  { name: 'Surge protector & ground check',                 system: 'electrical', from: 'essential' },

  // A/C & climate
  { name: 'A/C ON in every room (weekly)',                  system: 'climate', from: 'elite'     },
  { name: 'A/C coil deep cleaning',                         system: 'climate', from: 'premium'   },
  { name: 'Deep A/C service',                               system: 'climate', from: 'premium'   },
  { name: 'Dehumidifier & condensate drain check',          system: 'climate', from: 'premium'   },
  { name: 'A/C filter wash',                                system: 'climate', from: 'essential' },
  { name: 'Outdoor A/C unit cleaning',                      system: 'climate', from: 'essential' },
  { name: 'Surge protector review',                         system: 'climate', from: 'essential' },

  // Structure & exterior
  { name: 'Perimeter walkthrough (weekly)',                 system: 'structure', from: 'elite'     },
  { name: 'Exterior pressure washing (decks, walkways)',    system: 'structure', from: 'elite'     },
  { name: 'Glass partition cleaning',                       system: 'structure', from: 'premium'   },
  { name: 'Roof crack & joint check',                       system: 'structure', from: 'premium'   },
  { name: 'Motorized gate service',                         system: 'structure', from: 'premium'   },
  { name: 'Caulking & silicon resealing',                   system: 'structure', from: 'premium'   },
  { name: 'Patio drain check',                              system: 'structure', from: 'essential' },
  { name: 'Hinge & gate lubrication',                       system: 'structure', from: 'essential' },
  { name: 'Gutter cleaning',                                system: 'structure', from: 'essential' },
  { name: 'Tile caulk check - baths, kitchens',             system: 'structure', from: 'essential' },
  { name: 'Concrete joint check',                           system: 'structure', from: 'essential' },
  { name: 'Railing & handrail tightness check',             system: 'structure', from: 'essential' },
  { name: 'Window & door seal check',                       system: 'structure', from: 'essential' },
  { name: 'Roof inspection',                                system: 'structure', from: 'essential' },
  { name: 'Exterior paint review',                          system: 'structure', from: 'essential' },
  { name: 'Anti-termite review',                            system: 'structure', from: 'essential' },
  { name: 'Storm drainage check',                           system: 'structure', from: 'essential' },
  { name: 'Professional anti-termite treatment',            system: 'structure', from: 'essential' },
  { name: 'Motorized gate check',                           system: 'structure', from: 'essential' },
  { name: 'Moss accumulation check (decks, walksways, etc)', system: 'structure', from: 'essential' },
  { name: 'Roof waterproofing check',                       system: 'structure', from: 'essential' },

  // Pool & garden
  { name: 'Supervised routine landscaping',                 system: 'garden', from: 'elite'     },
  { name: 'Irrigation verification (weekly)',               system: 'garden', from: 'elite'     },
  { name: 'Pool pump filter cleaning',                      system: 'garden', from: 'elite'     },
  { name: 'Pool filter sand replacement',                   system: 'garden', from: 'elite'     },
  { name: 'Garden prep (pre-dry season)',                   system: 'garden', from: 'elite'     },
  { name: 'Full irrigation review (pre-dry season)',        system: 'garden', from: 'elite'     },
  { name: 'Irrigation calibration',                         system: 'garden', from: 'premium'   },
  { name: 'Irrigation check',                               system: 'garden', from: 'essential' },

  // Tech, security & wastewater
  { name: 'Professional septic emptying',                   system: 'tech', from: 'elite'     },
  { name: 'Range hood filter cleaning',                     system: 'tech', from: 'elite'     },
  { name: 'Alarm & camera test',                            system: 'tech', from: 'elite'     },
  { name: 'Solar panels cleaning',                          system: 'tech', from: 'elite'     },
  { name: 'septic check',                                   system: 'tech', from: 'premium'   },
  { name: 'Biological septic additive',                     system: 'tech', from: 'premium'   },

  // À la carte (on request) — quotable at any tier
  { name: 'Anti-termite spraying',                          system: 'alacarte', from: 'quote' },
  { name: 'Mosquito spray system',                          system: 'alacarte', from: 'quote' },
  { name: 'Mirror & picture hanging',                       system: 'alacarte', from: 'quote' },
  { name: 'Appliance installation',                         system: 'alacarte', from: 'quote' },
  { name: 'Water filtration system',                        system: 'alacarte', from: 'quote' },
  { name: 'Railing & handrail tightening',                  system: 'alacarte', from: 'quote' },
  { name: 'Crack & finish repair',                          system: 'alacarte', from: 'quote' },
  { name: 'NVR & camera firmware update',                   system: 'alacarte', from: 'quote' },
  { name: 'Router & modem firmware update',                 system: 'alacarte', from: 'quote' },
  { name: 'Surge protector installation',                   system: 'alacarte', from: 'quote' },
]
